import {observer} from "mobx-react";
import * as React from "react";
import {createContext, useContext, useEffect, useState} from "react";

import {useAdminApi} from "../api/api-context";
import {useAuthenticationStore} from "../stores/store-hooks";
import {getServerUrl} from "../utils/global-config";
import {IAquaEditor} from "./aqua-editor";
import {loadAquaEditor} from "./load-aqua-editor";

export type AquaEditorData = {
    [mediaId: string]:
        | {state: "uninitialized"}
        | {state: "loading"}
        | {state: "loaded"; aquaEditor: IAquaEditor}
        | {state: "error"; error: Error};
};

export type AquaEditorState = AquaEditorData[string]["state"];

export interface AquaEditorContextValue {
    aquaEditorData: AquaEditorData;
    setAquaEditorData: (mediaId: string, data: AquaEditorData[string]) => void;
    loadEditor: (mediaId: string, mediaUrl: string) => void;
}

export const AquaEditorContext = createContext<AquaEditorContextValue | undefined>(undefined);

type AquaEditorProviderProps = React.PropsWithChildren<{}>;

export const AquaEditorProvider: React.FC<AquaEditorProviderProps> = observer(({children}) => {
    const adminApi = useAdminApi();
    const authenticationStore = useAuthenticationStore();
    const username = authenticationStore.username;

    const [aquaEditorData, setAquaEditorData] = useState<AquaEditorData>({});

    const setEditorData = (mediaId: string, data: AquaEditorData[string]) => {
        setAquaEditorData((prevData) => ({
            ...prevData,
            [mediaId]: data,
        }));
    };

    // editorの読み込み
    const loadEditor = (mediaId: string, mediaUrl: string) => {
        // "uninitialized"ではない場合、またはusernameがない場合は何もしない
        const currentState = aquaEditorData[mediaId]?.state;
        if (currentState !== "uninitialized" || !username) {
            return;
        }

        const imagefrontTokenProvider = async () => {
            return (await authenticationStore.getOrRefreshToken())!;
        };

        // "loading"状態に変更
        setEditorData(mediaId, {state: "loading"});

        // aquaEditorをロードしてstateを"loaded"に変更
        loadAquaEditor({
            siteUrl: mediaUrl,
            serverUrl: getServerUrl(),
            adminApi,
            imagefrontTokenProvider,
            user: username,
        })
            .then((aquaEditor) => setEditorData(mediaId, {state: "loaded", aquaEditor}))
            // エラーが発生した場合はstateを"error"に変更
            .catch((error) => {
                console.error(error);
                setEditorData(mediaId, {state: "error", error});
            });
    };

    return (
        <AquaEditorContext.Provider
            value={{
                aquaEditorData,
                setAquaEditorData: setEditorData,
                loadEditor,
            }}
        >
            {children}
        </AquaEditorContext.Provider>
    );
});

export function useAquaEditorContext(mediaId: string, mediaUrl: string) {
    const context = useContext(AquaEditorContext);
    const [firstFlag, setFirstFlag] = useState(true);

    if (!context) {
        throw new Error("AquaEditorProvider is not provided");
    }

    const {aquaEditorData, loadEditor, setAquaEditorData} = context;

    const editorData = aquaEditorData[mediaId];

    useEffect(() => {
        if (!mediaId || !mediaUrl) return;
        if (!editorData) {
            // mediaId に対応するデータがない場合は初期化
            setAquaEditorData(mediaId, {state: "uninitialized"});
            return;
        }
        if (editorData.state === "error" && firstFlag) {
            // 初回エラー時は "uninitialized" に戻してリトライ
            setFirstFlag(false);
            setAquaEditorData(mediaId, {state: "uninitialized"});
            return;
        }
        if (editorData.state === "loaded") {
            if (editorData.aquaEditor.siteUrl !== mediaUrl) {
                // メディア URL が変わった場合は再初期化
                setAquaEditorData(mediaId, {state: "uninitialized"});
            }
            return;
        }
        if (editorData.state === "uninitialized" || editorData.state === "error") {
            loadEditor(mediaId, mediaUrl);
        }
    }, [mediaId, mediaUrl, aquaEditorData]); // eslint-disable-line react-hooks/exhaustive-deps

    return {loadEditor, aquaEditorData, setAquaEditorData};
}
